import { Component, EventEmitter, Input, Output } from '@angular/core';

export type AiTableSearchMode = 'ai' | 'name';

export interface AiTableSearchChange {
  term: string;
  mode: AiTableSearchMode;
}

/** Per-browser memory of the last mode picked, so the choice survives a reload. */
const MODE_STORAGE_KEY = 'aiTableSearchMode';

/**
 * Name search is the default: it is how the box always behaved, and it finds every record,
 * while AI search only finds assets that have been through the tagging service.
 */
const DEFAULT_MODE: AiTableSearchMode = 'name';

/** The mode this viewer last picked, or Name if they never picked one (or storage is unavailable). */
export function loadAiTableSearchMode(): AiTableSearchMode {
  try {
    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    return stored === 'ai' || stored === 'name' ? stored : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

function saveAiTableSearchMode(mode: AiTableSearchMode): void {
  try {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // Private windows and blocked storage: the toggle still works, it just isn't remembered.
  }
}

/**
 * Replaces the plain "Table Search" box on the Items and Challenges grids.
 *
 * It exists as its own component rather than a tweak to the existing input because the
 * query semantics genuinely differ: the old box ORs every field it touches, so typing a
 * second word widens the result set instead of narrowing it. This one ANDs terms, and
 * understands `style:zen` scoping.
 *
 * The name/fileName mode is kept deliberately, and is the default. AI search can only find
 * assets that have been through the tagging service, so until the back catalogue is
 * processed most records are invisible to it — and someone who types an exact file name
 * still needs a way to land on it. The parent grid owns the current mode and passes it in.
 */
@Component({
  selector: 'ai-table-search',
  templateUrl: './ai-table-search.component.html',
  styleUrls: ['./ai-table-search.component.sass'],
})
export class AiTableSearchComponent {
  /** Placeholder for the non-AI mode, so it can read "Table Search" as it always has. */
  @Input() fallbackPlaceholder = 'Table Search';

  @Input() term = '';

  @Input() mode: AiTableSearchMode = DEFAULT_MODE;

  @Output() searchChange = new EventEmitter<AiTableSearchChange>();

  readonly modeOptions: Array<{ label: string; value: AiTableSearchMode; tooltip: string }> = [
    { label: 'Name', value: 'name', tooltip: 'Search names and file names' },
    { label: 'AI', value: 'ai', tooltip: 'Search AI tags and descriptions' },
  ];

  get placeholder(): string {
    return this.mode === 'ai' ? 'Search AI tags & description' : this.fallbackPlaceholder;
  }

  onInput(): void {
    this.emit();
  }

  onModeChange(mode: AiTableSearchMode): void {
    this.mode = mode;
    saveAiTableSearchMode(mode);
    // Re-run immediately: the same text means something different in the other mode,
    // and leaving stale results on screen under a changed label is worse than a reload.
    this.emit();
  }

  clear(): void {
    this.term = '';
    this.emit();
  }

  private emit(): void {
    this.searchChange.emit({ term: this.term, mode: this.mode });
  }
}
